import { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { Button, Input, Stack, Text, Link } from "@chakra-ui/react";
import { useAuth } from "@/context/AuthContext";
import { colors } from "@/theme/colors";
import { AuthShell, Field, inputStyle, ErrorNote, PasswordInput } from "@/pages/auth/AuthShell";
import { consumeIdleSignoutFlag } from "@/lib/idle";

export default function Login() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [idleNote] = useState(() => consumeIdleSignoutFlag());

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    // navigator.onLine may INFORM (passive hint below) but NEVER GATE sign-in:
    // a phone that wrongly reports offline must still be able to attempt login.
    setLoading(true);
    try {
      await signIn(email.trim(), password);
      navigate("/");
    } catch (err) {
      const msg = /failed to fetch|networkerror|load failed/i.test(err.message || "")
        ? "You're offline — you need a connection to sign in."
        : (err.message || "Unable to sign in.");
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Sign in"
      subtitle="Welcome back. Enter your credentials to continue."
      footer={<>Accounts are created by invitation from your administrator.</>}
    >
      <form onSubmit={submit}>
        <Stack gap="4">
          {idleNote && (
            <Text px="3.5" py="2.5" borderRadius="10px" fontSize="sm" bg={colors.tealTint}
              border="1px solid" borderColor="#BFE3E8" color={colors.textPrimary} data-testid="idle-signout-note">
              You were signed out due to inactivity. Please sign in again.
            </Text>
          )}
          <ErrorNote>{error}</ErrorNote>
          {!navigator.onLine && !error && (
            <Text px="3.5" py="2.5" borderRadius="10px" fontSize="sm" bg="#FBEEDA" color="#8A5106" data-testid="login-offline-hint">
              You appear to be offline. You can still try to sign in — it needs a connection to complete.
            </Text>
          )}
          <Field label="Email">
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" {...inputStyle} />
          </Field>
          <Field label="Password">
            <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" />
          </Field>
          <Text textAlign="right" fontSize="sm">
            <Link as={RouterLink} to="/forgot-password" color={colors.tealHover} data-testid="go-forgot">
              Forgot password?
            </Link>
          </Text>
          <Button type="submit" w="full" loading={loading} data-testid="login-submit">
            Sign in
          </Button>
        </Stack>
      </form>
    </AuthShell>
  );
}
